import { DateTime } from 'luxon';
import { __, sprintf } from '~/javascripts/lib/locale';
import { isFutureDate } from '~/javascripts/lib/datetime';
import { GITLAB_CREDITS_PLAN_TYPE } from '~/javascripts/subscription/constants';
import {
  DATE_TENSE,
  RENEWAL_TYPE,
  SUBSCRIPTION_DATE_EXPIRED,
  SUBSCRIPTION_DATE_FUTURE_START,
  SUBSCRIPTION_DATE_PAST_START,
  SUBSCRIPTION_DATE_REACTIVATE,
  UNKNOWN_SUBSCRIPTION,
  PRODUCT_SUB_TYPES,
  EXCLUDED_PRODUCT_SUB_TYPES,
  UNMAPPED_PRODUCT_SUB_TYPE,
} from './constants';

const getPlanDateTextForFutureStartDate = (startDateLocale) => {
  if (!startDateLocale) {
    return null;
  }

  return `${DATE_TENSE.STARTS} on ${startDateLocale}`;
};

const getPlanDateTextForPastStartDate = (willAutoRenew, hasExpired, endDateLocale) => {
  if (!endDateLocale) {
    return null;
  }

  let dateTense;
  if (hasExpired) {
    dateTense = DATE_TENSE.EXPIRED;
  } else if (willAutoRenew) {
    dateTense = DATE_TENSE.AUTO_RENEWS;
  } else {
    dateTense = DATE_TENSE.EXPIRES;
  }

  // return `${dateTense} on ${endDateLocale}`;
  return `${dateTense} ${__('on')} ${endDateLocale}`;
};

export const getPlanDateText = ({
  willAutoRenew,
  hasFutureStartDate,
  hasExpired,
  startDateLocale,
  endDateLocale,
}) => {
  if (hasFutureStartDate) {
    return getPlanDateTextForFutureStartDate(startDateLocale);
  }

  return getPlanDateTextForPastStartDate(willAutoRenew, hasExpired, endDateLocale);
};

export const getSubscriptionTitle = ({
  namespace,
  mainProduct,
  addonProducts = [],
  allProducts = [],
}) => {
  return (
    namespace ||
    mainProduct?.name ||
    addonProducts[0]?.name ||
    allProducts[0]?.name ||
    UNKNOWN_SUBSCRIPTION
  );
};

const getExpiredTooltip = (endDate, renewalExpirationDate) => {
  if (!endDate) {
    return null;
  }

  if (renewalExpirationDate) {
    return sprintf(SUBSCRIPTION_DATE_REACTIVATE, { endDate, renewalExpirationDate });
  }
  return sprintf(SUBSCRIPTION_DATE_EXPIRED, { endDate });
};

const getUnexpiredTooltip = ({ willAutoRenew, hasFutureStart, startDate, endDate }) => {
  if (!startDate || !endDate) {
    return null;
  }

  const renewalType = willAutoRenew ? RENEWAL_TYPE.AUTO_RENEW : RENEWAL_TYPE.EXPIRE;
  const dateString = hasFutureStart ? SUBSCRIPTION_DATE_FUTURE_START : SUBSCRIPTION_DATE_PAST_START;

  return sprintf(dateString, { startDate, endDate, renewalType });
};

export const getDateTooltip = ({
  startDate,
  endDate,
  renewalExpirationDate,
  willAutoRenew,
  hasExpired,
  hasFutureStart,
}) => {
  if (hasExpired) {
    return getExpiredTooltip(endDate, renewalExpirationDate);
  }

  return getUnexpiredTooltip({ willAutoRenew, hasFutureStart, startDate, endDate });
};

export function getProductsBySubType(products) {
  const productSubTypesList = Object.values(PRODUCT_SUB_TYPES);

  return products.reduce((memo, product) => {
    const { type } = product;
    let { subType } = product;

    // EXCLUDED_PRODUCT_SUB_TYPES are products which have multiple ratePlans with the same `ratePlanId`.
    // Eg: 1 - Dedicated - Ultimate - 1 Year, 2 - Dedicated - Storage 10GB - 1 Year, 3 - Dedicated - Administration Fee [Small] - 1 Year
    // Although 2 & 3 are classified as addon products, they still need to be displayed in the main product section. Hence we need to ignore
    // these rate plans while filtering the addon product rate plans.
    if (type !== PRODUCT_SUB_TYPES.MAIN && EXCLUDED_PRODUCT_SUB_TYPES.includes(subType)) {
      return memo;
    }

    if (!productSubTypesList.includes(subType)) {
      subType = UNMAPPED_PRODUCT_SUB_TYPE;
    }

    const mappedType = type === PRODUCT_SUB_TYPES.MAIN ? PRODUCT_SUB_TYPES.MAIN : subType;

    if (!memo[mappedType]) {
      // eslint-disable-next-line no-param-reassign
      memo[mappedType] = [];
    }

    // eslint-disable-next-line no-param-reassign
    memo[mappedType] = [...memo[mappedType], product];

    return memo;
  }, {});
}

export function getUnmappedProductDetails(unmappedProductRatePlans, ratePlanCharges) {
  return unmappedProductRatePlans.reduce((productDetails, ratePlan) => {
    const productRatePlanCharges = ratePlanCharges.filter(
      (ratePlanCharge) => ratePlanCharge.ratePlanId === ratePlan.ratePlanId,
    );

    if (!ratePlan.skuId || !ratePlan.skuName || productRatePlanCharges.length === 0) {
      return productDetails;
    }
    // We can have multiple ratePlans of the same unmapped SKU. We want to store all of these against
    // a single key - `ratePlan.skuId`
    const currentProductDetail = productDetails[ratePlan.skuId];
    if (currentProductDetail) {
      currentProductDetail.ratePlans?.push(ratePlan);
      currentProductDetail.ratePlanCharges =
        currentProductDetail.ratePlanCharges?.concat(productRatePlanCharges);
    } else {
      // eslint-disable-next-line no-param-reassign
      productDetails[ratePlan.skuId] = {
        ratePlans: [ratePlan],
        ratePlanCharges: productRatePlanCharges,
        name: ratePlan.skuName,
      };
    }

    return productDetails;
  }, {});
}

/**
 * Calculates the current quantity from rate plan charges
 * @param {Array} ratePlanCharges - Array of rate plan charge objects
 * @param {Function} [quantityTransformer] - Optional function to transform quantity (e.g., multiply by 10 for storage)
 * @returns {number|null} Current quantity or null if cannot be determined
 */
export function calculateCurrentQuantity(ratePlanCharges, quantityTransformer = (q) => q) {
  if (!ratePlanCharges?.length) {
    return null;
  }

  const activeCharges = ratePlanCharges.filter((charge) => charge?.isActiveCharge === true);

  if (activeCharges.length) {
    return activeCharges.reduce(
      (total, { quantity = 0 }) => total + quantityTransformer(quantity),
      0,
    );
  }

  const firstCharge = ratePlanCharges[0];
  const lastCharge = ratePlanCharges[ratePlanCharges.length - 1];

  // If first charge is in the future, use its quantity
  if (isFutureDate(firstCharge?.startDate)) {
    return quantityTransformer(firstCharge?.quantity);
  }

  // If last charge is expired, use its quantity
  if (DateTime.local().diff(DateTime.fromISO(lastCharge?.endDate)) > 0) {
    return quantityTransformer(lastCharge?.quantity);
  }

  return null;
}

const typeToNameMap = {
  [GITLAB_CREDITS_PLAN_TYPE]: __('Buy GitLab credits'),
};

export function buildPathsObj(purchasePaths) {
  return purchasePaths.reduce((memo, { planType, ...paths }) => {
    if (!typeToNameMap[planType]) {
      return memo;
    }

    return { ...memo, [planType]: paths };
  }, {});
}

export function buildPurchasePathsObj(subscriptions) {
  return subscriptions.reduce((memo, { name, purchasePaths }) => {
    if (!purchasePaths.length) {
      return memo;
    }

    return { ...memo, [name]: buildPathsObj(purchasePaths) };
  }, {});
}

export function getActionsFromPaths(purchasePaths = {}, pathKey = 'newPath') {
  if (!purchasePaths || typeof purchasePaths !== 'object') {
    return [];
  }

  return Object.keys(purchasePaths).reduce((memo, type) => {
    const path = purchasePaths[type][pathKey];
    if (!typeToNameMap[type] || typeof path !== 'string') {
      return memo;
    }

    return [
      ...memo,
      {
        text: typeToNameMap[type],
        type,
        href: path,
        extraAttrs: {
          'data-testid': `purchase-action-${type}`,
        },
      },
    ];
  }, []);
}
